import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "goods-schedule-info" }
const _hoisted_2 = { key: 1 }

import AreaChartOutlined from '@ant-design/icons-vue/AreaChartOutlined';
import {
    ref,
    watch,
    computed,
} from 'vue';
import {
    IGoodEntity,
    IMedicationCourse,
    IMedication,
    IIntakeDependancy,
    IIntakeForms,
    IPatientDayActions,
    IPatientSchedule,
    IMedicationUnit,
} from '@tools/types';
import moment from 'moment';
import {
    getIntakeForms,
    getIntakeDependency,
    getPatientCommonDayActions,
    getPatientDayActions,
    getPatientSchedules,
    getMedicationUnits,
} from '@api';
import { Dictionary, keyBy } from 'lodash';
import {
    calcMedicationProduction,
    createMedicationsMap,
    createSubtypesMap,
    useScheduleStore,
} from '@tools/methods';
import { showErrorNotification } from '@/utils';

interface ITableRow {
    key: string,
    type: string,
    plannedTime: string,
    actualTime: string,
    description: string,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GoodsSchedule',
  props: {
    disabled: { type: Boolean },
    course: null,
    datePlanStart: null,
    medications: null,
    goods: null,
    patientId: null
  },
  setup(__props: any) {

const props = __props as {
    disabled?: boolean,
    course: IMedicationCourse,
    datePlanStart: ReturnType<typeof moment>,
    medications: IMedication[],
    goods: IGoodEntity[],
    patientId: number,
}



const productionData = ref<ITableRow[]>([]);
const scheduleData = ref<ReturnType< typeof calcMedicationProduction>>();
const subtypesMap = ref<ReturnType<typeof createSubtypesMap>>();
const medicationsMap = ref<ReturnType<typeof createMedicationsMap>>();
const medicationUnitsMap = ref<Dictionary<IMedicationUnit>>();

const medication = computed(
    () => medicationsMap.value?.[props.course.clinicMedicationId],
);
const medicationUnit = computed(() => {
    if (!medication.value) {
        return '';
    }
    return medicationUnitsMap.value?.[medication.value?.unitId].name;
});

const modalTitle = computed(() => {
    const title = 'График производства и приёма препарата:';
    if (!medicationsMap.value || !subtypesMap.value) {
        return `${title} загрузка...`;
    }
    return `${title} ${
        medicationsMap.value[props.course.clinicMedicationId]?.name}(${
        props.course.medicationSubtypeId
            ? subtypesMap.value[props.course.medicationSubtypeId]?.name || 'Без подтипа'
            : 'Без подтипа'})`;
});

let intakeForms: Dictionary<IIntakeForms>;
let intakeDependancy: Dictionary<IIntakeDependancy>;
let commonActions: IPatientDayActions;
let patientActions: IPatientDayActions;
let existingSchedule: IPatientSchedule[];

const productionInfo = computed(() => {
    if (scheduleData.value?.productionSchedule.length && props.course.productionFreq) {
        return `${Math.ceil(scheduleData.value.productionSchedule.length)}  раза`;
    }
    return 'Нет';
});

const columns = [
    { title: 'Тип', dataIndex: 'type' },
    { title: 'Плановое время', dataIndex: 'plannedTime' },
    { title: 'Фактическое время', dataIndex: 'actualTime' },
    { title: 'Описание приёма', dataIndex: 'description' },
];

const visible = ref(false);

watch([visible, () => props.course], async (values) => {
    const [isVisible, course] = values;
    if (!isVisible) {
        return;
    }
    if (!medicationUnitsMap.value) {
        try {
            medicationUnitsMap.value = keyBy((await getMedicationUnits()).data, 'id');
        } catch {
            showErrorNotification('Не удалось загрузить справочники.');
            return;
        }
    }
    if (!intakeForms) {
        try {
            intakeForms = keyBy((await getIntakeForms()).data, 'id');
            intakeDependancy = keyBy((await getIntakeDependency()).data, 'id');
        } catch {
            showErrorNotification('Не удалось загрузить справочники.');
            return;
        }
        try {
            commonActions = (await getPatientCommonDayActions()).data;
            patientActions = (await getPatientDayActions(props.patientId)).data;
            if (props.course.id) {
                existingSchedule = (await getPatientSchedules(
                    props.patientId,
                    { params: { courseId: props.course.id } },
                )).data;
            } else {
                existingSchedule = [];
            }
        } catch {
            showErrorNotification('Не удалось загрузить расписание пациента.');
            return;
        }
    }
    const scheduleStore = useScheduleStore(
        props.medications,
        commonActions,
        patientActions,
        props.goods,
    );
    subtypesMap.value = scheduleStore.subtypesMap;
    medicationsMap.value = scheduleStore.medicationsMap;
    scheduleData.value = calcMedicationProduction(
        course,
        props.datePlanStart,
    );
    const receptions = scheduleData.value.productionSchedule
        .map((schedulePeriod, preiodIdx) => schedulePeriod.map((scheduleEntry, entryIdx) => ({
            key: `reception-${preiodIdx}-${entryIdx}`,
            type: props.course.productionFreq
                ? `Приём (б/м ${
                    moment(scheduleData.value?.courseDoses[preiodIdx].date).format('DD.MM.YYYY')
                } - ${entryIdx + 1})`
                : 'Приём',
            plannedTime: scheduleEntry.timeTake,
            actualTime: existingSchedule.length && existingSchedule[entryIdx]?.checked
                ? moment(existingSchedule[entryIdx].timeTake).format('DD.MM.YYYY HH:mm')
                : '',
            description: `Дозировка ${
                scheduleEntry.dose.medicationPortion} ${medicationUnit.value}, ${
                intakeForms[scheduleEntry.dose.intakeFormId]?.name || ''} ${
                intakeDependancy[scheduleEntry.dose.intakeDependencyId].name}`,
        }))).flat();
    const biomaterialTakes = !props.course.productionFreq
        ? []
        : scheduleData.value.courseDoses.map((scheduleEntry, i) => ({
            key: `biomaterial-take-${i}`,
            type: `Сдача биоматериала для: ${
                scheduleEntry.dose.toFixed(1)} услуг, ${
                scheduleData.value?.productionSchedule[i].length
            } приёмов`,
            plannedTime: moment(scheduleEntry.date).startOf('day').toDate(),
            actualTime: '',
            description: '',
        }));
    productionData.value = receptions.concat(biomaterialTakes)
        .sort((a, b) => new Date(a.plannedTime).getTime() - new Date(b.plannedTime).getTime())
        .map((scheduleEntry) => ({
            ...scheduleEntry,
            plannedTime: scheduleEntry.type === 'Приём'
                ? moment(scheduleEntry.plannedTime).format('DD.MM.YYYY HH:mm')
                : moment(scheduleEntry.plannedTime).format('DD.MM.YYYY'),
        }));
}, { deep: true });

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_button, {
      "data-test-id": "schedule-show-btn",
      disabled: __props.disabled,
      size: "small",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (visible.value = true))
    }, {
      icon: _withCtx(() => [
        _createVNode(_unref(AreaChartOutlined))
      ]),
      _: 1
    }, 8, ["disabled"]),
    (visible.value)
      ? (_openBlock(), _createBlock(_component_a_modal, {
          key: 0,
          visible: "",
          width: 1200,
          title: _unref(modalTitle),
          "ok-text": "Закрыть",
          "cancel-button-props": {class: {
            'goods-schedule-modal__cancel-btn': true,
        }},
          class: "goods-schedule",
          onOk: _cache[1] || (_cache[1] = ($event: any) => (visible.value = false)),
          onCancel: _cache[2] || (_cache[2] = ($event: any) => (visible.value = false))
        }, {
          default: _withCtx(() => [
            (__props.course && productionData.value.length)
              ? (_openBlock(), _createBlock(_component_a_form, { key: 0 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("div", null, " Длительность курса: " + _toDisplayString(__props.course.daysTakePeriod) + " дней ", 1),
                      _createElementVNode("div", null, " Производство: " + _toDisplayString(_unref(productionInfo)), 1)
                    ]),
                    _createVNode(_component_a_table, {
                      columns: columns,
                      "data-source": productionData.value
                    }, null, 8, ["data-source"])
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createElementBlock("div", _hoisted_2, " Нет данных. "))
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})